import React from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import DOMPurify from 'dompurify';
import Button from '../presentational/common/Button';

// Cabeçalho de grupo: o nome da camada/tipo de feature (ex.: CONDUTA_BAIXA).
const GroupHeading = styled.h3`
  margin: 12px 0 6px;
  font-size: 100%;
  font-weight: bold;
  color: #0080B6;
`;

// Um cartão por feature, com moldura e espaço entre cartões, para distinguir
// claramente cada feature quando o clique devolve várias.
const FeatureCard = styled.div`
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
`;

const FeatureCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 8px;
  background: #eee;
  border-bottom: 1px solid #ddd;
  font-size: 90%;
  font-weight: bold;
`;

// Tabela transposta (nome do campo | valor) dentro de cada cartão.
const FeatureInfoTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 90%;

  th, td {
    text-align: left;
    vertical-align: top;
    padding: 3px 8px;
    border-top: 1px solid #eee;
  }

  tr:first-child th, tr:first-child td {
    border-top: 0;
  }

  th {
    font-weight: bold;
    background: #fafafa;
    white-space: nowrap;
    width: 1%;
  }

  td {
    word-break: break-word;
  }

  a {
    color: blue;
    word-break: break-all;
  }
`;

const TableContainerDiv = styled.div`
  // horizontal scroll only when a wide value needs it; the modal owns vertical
  // scrolling, so this avoids a second (always-on) scrollbar
  overflow-x: auto;
  padding: 8px 0 0;
`;

// Centered dialog sized to the content, instead of react-modal's default
// full-screen inset (which left a white band at the top and a mostly-empty sheet).
const MODAL_STYLE = {
  overlay: { zIndex: 1000, backgroundColor: 'rgba(0, 0, 0, 0.4)' },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 40px)',
    maxWidth: '520px',
    maxHeight: '85vh',
    overflow: 'auto',
    padding: '12px 16px',
    borderRadius: '6px',
  },
};

// Server-rendered GetFeatureInfo cards (e.g. a QGIS Server text/html maptip).
// Constrain images and give related-record tables the same look as the JSON ones.
const FeatureInfoHtml = styled.div`
  margin-bottom: 10px;

  img {
    max-width: 100%;
    height: auto;
  }

  table {
    border-collapse: collapse;
    font-size: 90%;
  }

  td, th {
    border: 1px solid #ddd;
    padding: 2px 6px;
  }
`;

const FeatureDisplayer = ({ features, htmlBlocks = [], featureFocus, handleModalClose }) => {
  const groupedFeatures = features.reduce((groupedFeatures, feature) => {
    const [featureType,] = feature.id.split('.');

    if (!(featureType in groupedFeatures)) {
      groupedFeatures[featureType] = [];
    }

    groupedFeatures[featureType].push(feature);

    return groupedFeatures;
  }, {});

  const getUrlField = (feature, prop) => {
    return <a href={feature.properties[prop]} target="_blank">{feature.properties[prop]}</a>
  }

  const renderFeatureValue = (feature, prop) => {
    const value = feature.properties[prop];
    const isString = typeof value === 'string';
    const isURL = isString && (value.startsWith('http://') || value.startsWith('https://'));

    return isURL ? getUrlField(feature, prop) : value;
  }

  return (<Modal isOpen={true} ariaHideApp={false} style={MODAL_STYLE}>
    <Button onClick={handleModalClose}>
      <i className="fas fa-times" />
    </Button>
    <TableContainerDiv>
      {htmlBlocks.map((html, index) => (
        <FeatureInfoHtml
          key={`html-block-${index}`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
        />
      ))}
      {Object.entries(groupedFeatures).map(([featureType, groupFeatures]) => (
        <div key={featureType}>
          <GroupHeading>{featureType}</GroupHeading>
          {groupFeatures.map((feature, index) => (
            <FeatureCard key={feature.id}>
              <FeatureCardHeader>
                <span>{groupFeatures.length > 1 && `${index + 1} de ${groupFeatures.length}`}</span>
                <Button onClick={() => featureFocus(feature)}>Focar</Button>
              </FeatureCardHeader>
              <FeatureInfoTable>
                <tbody>
                  {Object.keys(feature.properties).map(prop => (
                    <tr key={`${feature.id}-${prop}`}>
                      <th>{prop}</th>
                      <td>{renderFeatureValue(feature, prop)}</td>
                    </tr>
                  ))}
                </tbody>
              </FeatureInfoTable>
            </FeatureCard>
          ))}
        </div>
      ))}
    </TableContainerDiv>
  </Modal>);
};

export default FeatureDisplayer;
